import type { Capability } from "@redotech/account-common/capability";
import type { UserPermissions } from "@redotech/account-common/permissions";
import type { TeamType } from "@redotech/account-common/team-type";
import { teamContext } from "@redotech/feature-flags-model/context";
import { featureFlag } from "@redotech/openfeature-contracts/flag";
import { analyticsLegacyReportsSettingsFlag } from "@redotech/redo-fulfillment-common/oms-feature-flags";
import { assertNever } from "@redotech/util/type";

/** The QuickBooks accounting settings entry for IMS teams. */
export const imsQuickbooksFlag = featureFlag(
  "ims-quickbooks",
  teamContext,
).boolean(false);

/** Feature flags settings visibility reads; closed so the facts hook must load each one. */
export const SETTINGS_FLAGS = {
  analyticsLegacyReportsSettings: analyticsLegacyReportsSettingsFlag,
  imsQuickbooks: imsQuickbooksFlag,
} as const;
export type SettingsFlag = keyof typeof SETTINGS_FLAGS;

/** Which settings UI generation the merchant is on, while two are live. */
export type SettingsExperience = never;

/** Team facts that are neither a capability nor a permission. */
export type SettingsFact =
  | "redoSuppliesTeam"
  | "restrictedShippingTeam"
  | "settingsReportsPageRetired"
  | "shopifyEmailTeam"
  | "supportAiSetting"
  | "warrantyRegistrationRewards"
  | "wmsSourceOfTruth";

export type SettingsCondition =
  | { kind: "always" }
  | { kind: "capability"; capability: Capability }
  | { kind: "permission"; permission: UserPermissions }
  | { kind: "flag"; flag: SettingsFlag }
  | { kind: "experience"; experience: SettingsExperience }
  | { kind: "teamType"; teamType: TeamType }
  | { kind: "platform"; platform: "shopify" }
  | { kind: "staff" }
  | { kind: "fact"; fact: SettingsFact }
  | { kind: "all"; conditions: readonly SettingsCondition[] }
  | { kind: "any"; conditions: readonly SettingsCondition[] }
  | { kind: "not"; condition: SettingsCondition };

export interface SettingsVisibilityFacts {
  capabilities: ReadonlySet<Capability>;
  permissions: ReadonlySet<UserPermissions>;
  flags: ReadonlySet<SettingsFlag>;
  experiences: ReadonlySet<SettingsExperience>;
  facts: ReadonlySet<SettingsFact>;
  teamType: TeamType | null;
  platform: string | null;
  staff: boolean;
}

export const always: SettingsCondition = { kind: "always" };
export const capability = (c: Capability): SettingsCondition => ({
  kind: "capability",
  capability: c,
});
export const permission = (p: UserPermissions): SettingsCondition => ({
  kind: "permission",
  permission: p,
});
export const flag = (f: SettingsFlag): SettingsCondition => ({
  kind: "flag",
  flag: f,
});
export const experience = (e: SettingsExperience): SettingsCondition => ({
  kind: "experience",
  experience: e,
});
export const teamType = (t: TeamType): SettingsCondition => ({
  kind: "teamType",
  teamType: t,
});
export const staff: SettingsCondition = { kind: "staff" };
export const fact = (f: SettingsFact): SettingsCondition => ({
  kind: "fact",
  fact: f,
});
export const all = (...conditions: SettingsCondition[]): SettingsCondition => ({
  kind: "all",
  conditions,
});
export const any = (...conditions: SettingsCondition[]): SettingsCondition => ({
  kind: "any",
  conditions,
});
export const not = (condition: SettingsCondition): SettingsCondition => ({
  kind: "not",
  condition,
});

export function evaluateSettingsCondition(
  condition: SettingsCondition,
  facts: SettingsVisibilityFacts,
): boolean {
  switch (condition.kind) {
    case "always":
      return true;
    case "capability":
      return facts.capabilities.has(condition.capability);
    case "permission":
      return facts.permissions.has(condition.permission);
    case "flag":
      return facts.flags.has(condition.flag);
    case "experience":
      return facts.experiences.has(condition.experience);
    case "teamType":
      return facts.teamType === condition.teamType;
    case "platform":
      return facts.platform === condition.platform;
    case "staff":
      return facts.staff;
    case "fact":
      return facts.facts.has(condition.fact);
    case "all":
      return condition.conditions.every((c) =>
        evaluateSettingsCondition(c, facts),
      );
    case "any":
      return condition.conditions.some((c) =>
        evaluateSettingsCondition(c, facts),
      );
    case "not":
      return !evaluateSettingsCondition(condition.condition, facts);
    default:
      return assertNever(condition);
  }
}

/** Three-valued result: "unknown" when the answer depends on team or product state. */
type SettingsAccessVerdict = "allowed" | "denied" | "unknown";

/** What a route guard knows about the user, without team or product state. */
export interface SettingsUserFacts {
  permissions: ReadonlySet<UserPermissions>;
  staff: boolean;
}

/** Evaluates with only user facts known; "denied" means no team state could make it pass. */
export function settingsAccessVerdict(
  condition: SettingsCondition,
  user: SettingsUserFacts,
): SettingsAccessVerdict {
  switch (condition.kind) {
    case "always":
      return "allowed";
    case "permission":
      return user.permissions.has(condition.permission) ? "allowed" : "denied";
    case "staff":
      return user.staff ? "allowed" : "denied";
    case "capability":
    case "flag":
    case "experience":
    case "teamType":
    case "platform":
    case "fact":
      return "unknown";
    case "all": {
      const verdicts = condition.conditions.map((c) =>
        settingsAccessVerdict(c, user),
      );
      if (verdicts.includes("denied")) {
        return "denied";
      }
      return verdicts.includes("unknown") ? "unknown" : "allowed";
    }
    case "any": {
      const verdicts = condition.conditions.map((c) =>
        settingsAccessVerdict(c, user),
      );
      if (verdicts.includes("allowed")) {
        return "allowed";
      }
      return verdicts.includes("unknown") ? "unknown" : "denied";
    }
    case "not": {
      const verdict = settingsAccessVerdict(condition.condition, user);
      if (verdict === "unknown") {
        return "unknown";
      }
      return verdict === "allowed" ? "denied" : "allowed";
    }
    default:
      return assertNever(condition);
  }
}
